import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AuthService } from "../../../core/services/auth.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiLink,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiNavigation } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { IAdmin } from "../../../model/interface/user";
import { AdminService } from "../../../core/services/admin.service";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";

@Component({
  selector: "app-single-user",
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiDataListDropdownManager,
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: "./single-user.component.html",
  styleUrl: "./single-user.component.scss",
  providers: [DatePipe],
})
export class SingleUserComponent implements OnInit {
  protected breadcrumbs: string[] = ["Royal Hospital", "Users"];
  userId: string | null = null;
  user: any;
  isLoading: boolean = false;
  loggedInUserCanEdit = false;
  private authService = inject(AuthService);

  constructor(
    private userService: AdminService,
    private resetPasswordService: ForgotResetPasswordService,
    private router: Router,
    private route: ActivatedRoute,
    private alerts: TuiAlertService,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {
    this.userId = this.route.snapshot.paramMap.get("id");
    if (this.userId) {
      this.loadUserData();
    }

    this.checkUserPermission();
  }

  loadUserData(): void {
    this.isLoading = true;
    if (this.userId) {
      this.userService.getOneUserById(this.userId).subscribe((res: any) => {
        console.log(res);
        this.user = res;
        this.breadcrumbs = ["Royal Hospital", "Users", this.user?.refNo ?? ""];
        this.isLoading = false;
      });
    }
  }

  navigateTo(index: number): void {
    switch (index) {
      case 0:
        this.router.navigate(["/admin/users"]);
        break;
      case 1:
        this.router.navigate(["/admin/users"]);
        break;
    }
  }

  private readonly dialogs = inject(TuiDialogService);
  protected size: TuiSizeL | TuiSizeS = "s";
  protected open = false;

  resetUserPassword(email: any): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPassword(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to user's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isLoading = true;
    if (!this.user) return;

    const updatedUser = { ...this.user, active: !this.user.active };

    this.userService.updateUser(updatedUser).subscribe({
      next: (response) => {
        this.user = response;
        const status = updatedUser.active ? "actived" : "deactivated";
        this.alerts
          .open(`User has been ${status} successfully.`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadUserData();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error updating user data:", error);
        this.alerts
          .open("Failed to update user data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  toggleArchive(): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isLoading = true;
    if (!this.user) return;

    const updatedUser = { ...this.user, archived: !this.user.archived };

    this.userService.updateUser(updatedUser).subscribe({
      next: (response) => {
        this.user = response;
        const status = updatedUser.archived ? 'archived' : 'unarchived';
        this.alerts
          .open(`User has been ${status} successfully.`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadUserData();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error updating user data:", error);
        this.alerts
          .open("Failed to update user data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("USER_UPDATE");
  }
}
